import * as React from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useRegistration } from '../hooks/useRegistration';
import { createRegistrationFlow, getRegistrationFlow } from '@/lib/api/kratos';
import type { RegistrationFlow } from '@/lib/api/kratos';

/**
 * RegistrationPage Component
 * 
 * Handles user registration using Ory Kratos flows
 * Renders dynamic form based on registration flow configuration
 */
export function RegistrationPage() {
  const [searchParams] = useSearchParams();
  const flowId = searchParams.get('flow');

  
  // UI State - matching original Nuxt implementation exactly
  const [fillEmail, setFillEmail] = React.useState(true);
  const [methodSelector, setMethodSelector] = React.useState(false);
  const [registerWebAuthn, setRegisterWebAuthn] = React.useState(false);
  const [registerPassword, setRegisterPassword] = React.useState(false);
  
  // Form data - matching original exactly
  const [identifier, setIdentifier] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [keyName, setKeyName] = React.useState('');
  const [csrfToken, setCsrfToken] = React.useState('');
  const [webAuthnLink, setWebAuthnLink] = React.useState('');
  const [passkeyCreateData, setPasskeyCreateData] = React.useState('');
  const [method, setMethod] = React.useState('');
  
  const registrationMutation = useRegistration();

  // Fetch or create registration flow
  const { data: flow, isLoading, error, refetch } = useQuery({
    queryKey: ['registrationFlow', flowId],
    queryFn: async (): Promise<RegistrationFlow> => {
      if (flowId) {
        try {
          return await getRegistrationFlow(flowId);
        } catch (error) {
          console.warn('Failed to get existing flow, creating new one:', error);
          return await createRegistrationFlow();
        }
      }
      return await createRegistrationFlow();
    },
    retry: 1,
    staleTime: 0,
  });

  // Load WebAuthn script from flow - matching LoginPage approach
  React.useEffect(() => {
    if (!flow) return;

    // Check if we have a WebAuthn script node and load it
    const webauthnNode = flow.ui.nodes.find((node: any) => 
      node.type === 'script' && (node.group === 'webauthn' || node.group === 'passkey')
    );

    if (webauthnNode && !document.getElementById('webauthn_script')) {
      console.log('Loading WebAuthn script from flow...', webauthnNode);
      const script = document.createElement('script');
      script.id = 'webauthn_script';
      const attrs = webauthnNode.attributes as any;
      script.src = attrs.src as string;
      script.async = attrs.async as boolean;
      if (attrs.crossorigin) {
        script.crossOrigin = attrs.crossorigin as string;
      }
      if (attrs.integrity) {
        script.integrity = attrs.integrity as string;
      }
      if (attrs.referrerpolicy) {
        script.referrerPolicy = attrs.referrerpolicy as string;
      }
      if (attrs.nonce) {
        script.nonce = attrs.nonce as string;
      }
      
      script.onload = () => {
        console.log('WebAuthn script loaded successfully from flow');
        console.log('Available window functions:', Object.keys(window).filter(key => key.includes('ory') || key.includes('Passkey')));
      };
      script.onerror = () => {
        console.error('Failed to load WebAuthn script from flow');
      };
      
      document.head.appendChild(script);
      console.log('WebAuthn script added to document head');
    }
  }, [flow]);

  // Debug DOM elements when WebAuthn is active
  React.useEffect(() => {
    if (registerWebAuthn && passkeyCreateData && webAuthnLink) {
      console.log('WebAuthn is active, checking DOM elements:');
      console.log('passkey_create_data element:', document.getElementById('passkey_create_data'));
      console.log('passkey_create_data by name:', document.getElementsByName('passkey_create_data')[0]);
      console.log('Form element:', document.getElementById('registration'));
      console.log('WebAuthn link:', webAuthnLink);
      
      const passkeyEl = document.getElementById('passkey_create_data');
      if (passkeyEl) {
        console.log('passkey_create_data value:', passkeyEl.getAttribute('value'));
        console.log('passkey_create_data form:', passkeyEl.closest('form'));
      }
    }
  }, [registerWebAuthn, passkeyCreateData, webAuthnLink]);

  // Handle flow response - matching original logic exactly
  React.useEffect(() => {
    if (!flow) return;

    console.log('Registration flow state:', flow);

    // Handle redirects for invalid flows - matching original
    if ([403, 404, 410].includes((flow as any).status)) {
      window.location.href = '/auth/registration';
      return;
    }

    // Extract flow data - matching original extractFlowData method
    const nodes = flow.ui.nodes;
    const identifierNode = nodes.find((n: any) => n.attributes.name === 'identifier');
    const csrfNode = nodes.find((n: any) => n.attributes.name === 'csrf_token');
    const passkeyDataNode = nodes.find((n: any) => n.attributes.name === 'passkey_create_data');
    const webAuthnNode = nodes.find((n: any) => n.attributes.name === 'passkey_register_trigger');

    setIdentifier((identifierNode?.attributes as any)?.value ? String((identifierNode?.attributes as any).value) : '');
    setCsrfToken((csrfNode?.attributes as any)?.value ? String((csrfNode?.attributes as any).value) : '');
    setPasskeyCreateData((passkeyDataNode?.attributes as any)?.value ? String((passkeyDataNode?.attributes as any).value) : '');
    setWebAuthnLink((webAuthnNode?.attributes as any)?.onclick ? String((webAuthnNode?.attributes as any).onclick) : '');
  }, [flow]);

  // Step handlers - matching original exactly
  const filledEmail = () => {
    if (identifier !== '') {
      setMethodSelector(true);
      setFillEmail(false);
    }
  };

  const passwordlessSelected = () => {
    setMethodSelector(false);
    setRegisterWebAuthn(true);
    setMethod('passkey');
  };

  const passwordSelected = () => {
    setMethodSelector(false);
    setRegisterPassword(true);
    setMethod('password');
  };

  // Get UI content based on current state - matching original
  const getTitle = () => {
    if (webAuthnLink && registerWebAuthn) return 'Account Registration';
    return 'Create Account';
  };

  const getDescription = () => {
    if (fillEmail) return 'Enter your email';
    if (registerPassword) return 'Enter your password';
    if (methodSelector) return 'Choose your access method';
    if (webAuthnLink && registerWebAuthn) return 'Use your Key, Fingerprint or Face Recognition to create your Access Key';
    return 'Join us today! Create your account to get started.';
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardContent className="flex items-center justify-center py-8">
            <div className="text-sm text-muted-foreground">Loading...</div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !flow) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Error</CardTitle>
            <CardDescription>Failed to load registration form</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {error instanceof Error ? error.message : 'An unexpected error occurred'}
            </p>
            <Button onClick={() => refetch()} className="w-full">
              Try Again
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-light">{getTitle()}</CardTitle>
          <CardDescription className="text-base">{getDescription()}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Render Kratos form - required for WebAuthn authentication */}
          {flow && (
            <form 
              id="kratos-form"
              action={flow.ui.action}
              method={flow.ui.method}
              style={{ display: 'none' }}
              onSubmit={(e) => {
                e.preventDefault();
                console.log('Kratos form submitted - handling via React');
                // Handle form submission via our React logic
                const formData = new FormData(e.currentTarget);
                const values: any = {};
                formData.forEach((value, key) => {
                  values[key] = value;
                });
                console.log('Form values:', values);
                
                // Submit via our React mutation
                registrationMutation.mutate({
                  flowId: flow.id,
                  values: {
                    method: values.method || 'passkey',
                    traits: {
                      email: values['traits.email'] || identifier
                    },
                    password: '', // Not needed for passkey
                    csrf_token: values.csrf_token,
                    // Add passkey-specific fields as needed
                    ...(values.passkey_create_data && { passkey_create_data: values.passkey_create_data }),
                    ...(values.passkey_register && { passkey_register: values.passkey_register }),
                    ...(values.webauthn_register_displayname && { webauthn_register_displayname: values.webauthn_register_displayname || keyName })
                  }
                });
              }}
            >
              {flow.ui.nodes
                .filter((node: any) => node.type === 'input')
                .map((node: any, index: number) => {
                  const attrs = node.attributes;
                  let value = String(attrs.value || '');
                  
                  // Update values with current React state
                  if (attrs.name === 'traits.email') {
                    value = identifier;
                  } else if (attrs.name === 'csrf_token') {
                    value = csrfToken;
                  } else if (attrs.name === 'passkey_create_data') {
                    value = passkeyCreateData;
                  } else if (attrs.name === 'method') {
                    value = method;
                  } else if (attrs.name === 'webauthn_register_displayname') {
                    value = keyName;
                  }
                  
                  return (
                    <input
                      key={index}
                      type={attrs.type as string}
                      name={attrs.name as string}
                      value={value}
                      disabled={attrs.disabled as boolean}
                      required={attrs.required as boolean}
                      readOnly={true}
                      style={{ display: attrs.type === 'hidden' ? 'none' : 'block' }}
                    />
                  );
                })}
            </form>
          )}

          {/* User-facing form - matching original exactly */}
          <form 
            id="registration"
            action={flow.ui.action} 
            method="post" 
            encType="application/x-www-form-urlencoded"
            onSubmit={() => registrationMutation.isPending}
          >
            {/* Hidden fields - matching original */}
            <input
              type="hidden"
              id="passkey_create_data"
              name="passkey_create_data"
              value={passkeyCreateData || ''}
              required
              readOnly
            />
            
            <input
              type="hidden"
              id="csrf_token"
              name="csrf_token"
              value={csrfToken}
              required
              readOnly
            />
            
            <input
              type="hidden"
              id="method"
              name="method"
              value={method}
            />

            {registerWebAuthn && (
              <input
                type="hidden"
                id="passkey_register"
                name="passkey_register"
                value=""
                readOnly
              />
            )}

            {/* Email input step - matching original */}
            {fillEmail && (
              <div className="space-y-4">
                <Input
                  type="email"
                  name="traits.email"
                  placeholder="Email"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  disabled={registrationMutation.isPending}
                  autoComplete="off"
                  required
                />
                <Button 
                  type="button"
                  onClick={filledEmail}
                  className="w-full h-12 text-base font-normal" 
                  disabled={registrationMutation.isPending || !identifier}
                >
                  Continue
                </Button>
              </div>
            )}

            {/* Method Selector - matching original */}
            {methodSelector && (
              <div className="space-y-4">
                <Button 
                  type="button"
                  onClick={passwordlessSelected}
                  className="w-full h-12 text-base font-normal"
                  variant="default"
                >
                  Passwordless
                </Button>
                <Button 
                  type="button"
                  onClick={passwordSelected}
                  className="w-full h-12 text-base font-normal"
                  variant="outline"
                >
                  Password
                </Button>
              </div>
            )}

            {/* Password Form - matching original */}
            {registerPassword && (
              <div className="space-y-4">
                <Input
                  type="password"
                  name="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={registrationMutation.isPending}
                  autoComplete="new-password"
                  required
                />
                <Button 
                  type="submit" 
                  className="w-full h-12 text-base font-normal" 
                  disabled={registrationMutation.isPending || !password}
                >
                  {registrationMutation.isPending ? 'Creating Account...' : 'Continue'}
                </Button>
              </div>
            )}

            {/* WebAuthn Form - matching original */}
            {registerWebAuthn && (
              <div className="space-y-4">
                <div className="text-center space-y-2">
                  <div className="text-sm text-muted-foreground">
                    Use your Key, Fingerprint or Face Recognition to create your Access Key
                  </div>
                </div>
                
                <Input
                  type="text"
                  name="webauthn_register_displayname"
                  placeholder="Security Key name"
                  value={keyName}
                  onChange={(e) => setKeyName(e.target.value)}
                  disabled={registrationMutation.isPending}
                  autoComplete="off"
                  required
                />

                {/* Use native button with onclick attribute - exactly like original Vue code */}
                <button
                  type="button"
                  name="passkey_register_trigger"
                  ref={(el) => {
                    if (el && webAuthnLink) {
                      // Set onclick attribute directly as string - matching original Vue :onclick
                      el.setAttribute('onclick', webAuthnLink);
                      console.log('Set onclick attribute on button:', webAuthnLink);
                    }
                  }}
                  onClick={() => {
                    // Additional debugging when button is clicked
                    console.log('Button clicked, checking DOM state:');
                    console.log('passkey_create_data element:', document.getElementById('passkey_create_data'));
                    console.log('passkey_create_data value:', document.getElementById('passkey_create_data')?.getAttribute('value'));
                    console.log('Form:', document.getElementById('registration'));
                  }}
                  className="w-full h-12 text-base font-normal bg-primary text-primary-foreground hover:bg-primary/90 rounded-md disabled:opacity-50 disabled:pointer-events-none"
                  disabled={registrationMutation.isPending || !keyName}
                >
                  {registrationMutation.isPending ? 'Creating Account...' : 'Continue'}
                </button>
              </div>
            )}
          </form>

          {/* Link to login page */}
          <div className="text-center">
            <div className="text-sm text-muted-foreground">
              Already have an account?{' '}
              <Link 
                to="/auth/login" 
                className="text-primary hover:underline"
              >
                Sign in
              </Link>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
